/**
 * Pure numeric helpers for the number-list cell charts: no React, recharts, or
 * formatting, so the tricky logic — bin edges, degenerate ranges — is directly
 * unit-testable, the same way charts/aggregation.ts and charts/format.ts are
 * tested. All functions assume a non-empty list; callers gate on
 * `values.length > 0`.
 */

/** A histogram bin: a `[x0, x1)` value range, closed on the last bin, and its count. */
export type Bin = {x0: number; x1: number; count: number};

export type Summary = {min: number; max: number; mean: number; count: number};

/**
 * Min/max of the list. With `includeZero`, the zero baseline is folded into the
 * range so an all-positive or all-negative list still plots against 0 — the
 * domain both bar renderers want.
 */
export const extent = (
  values: number[],
  opts?: {includeZero?: boolean}
): [number, number] => {
  let min = opts?.includeZero ? 0 : values[0];
  let max = opts?.includeZero ? 0 : values[0];
  for (const v of values) {
    if (v < min) {
      min = v;
    }
    if (v > max) {
      max = v;
    }
  }
  return [min, max];
};

export const summarize = (values: number[]): Summary => {
  let min = values[0];
  let max = values[0];
  let sum = 0;
  for (const v of values) {
    if (v < min) {
      min = v;
    }
    if (v > max) {
      max = v;
    }
    sum += v;
  }
  return {min, max, mean: sum / values.length, count: values.length};
};

const MIN_BINS = 5;
const MAX_BINS = 20;

/**
 * Equal-width value bins with counts. The bin count scales with √n, clamped to
 * [MIN_BINS, MAX_BINS], so the chart stays readable from ~25 up to thousands of
 * values. An all-equal list collapses to a single bin.
 */
export const computeBins = (values: number[]): Bin[] => {
  const [min, max] = extent(values);
  if (min === max) {
    return [{x0: min, x1: max, count: values.length}];
  }
  const binCount = Math.max(
    MIN_BINS,
    Math.min(MAX_BINS, Math.ceil(Math.sqrt(values.length)))
  );
  const width = (max - min) / binCount;
  const counts = Array.from({length: binCount}, () => 0);
  for (const v of values) {
    // Clamp the max value into the last bin; otherwise (v - min) / width === binCount.
    const idx = Math.min(binCount - 1, Math.floor((v - min) / width));
    counts[idx]++;
  }
  return counts.map((count, i) => ({
    x0: min + i * width,
    x1: i === binCount - 1 ? max : min + (i + 1) * width,
    count,
  }));
};
